import { useEffect } from 'react';
import { useTheme } from '@ai-content-hub/hooks';

/**
 * EAA-755: push light/dark toggles into every ACH embed iframe on the page.
 *
 * An embed (`/embed/player`, `/embed/editor`, `/embed/ai`) is its own document
 * on its own origin: it cannot read the host's `app-theme-mode`, and
 * `data-theme` — which both the design tokens and the H5P `ach-player.css`
 * override sheet key their dark palette off — is stamped per document. The
 * initial mode therefore travels as a `themeMode=` query param.
 *
 * The param cannot carry LATER toggles: changing it changes the iframe `src`,
 * which reloads the embed and throws away a learner's in-progress answers. So
 * the URL stays frozen after mount and subsequent toggles are pushed as a
 * message, which `ThemeInitializer` in the editor app applies in place.
 *
 * The sweep is over the live DOM rather than a ref list because the embeds are
 * mounted by four unrelated components (ContentDetailsEditor's view-session
 * iframe, IframeBlobWrapper, ACHAIGenerator, ProgramPreviewPlayer) and none of
 * them shares a parent that could hold that list.
 */
export function useEmbedThemeModeSync(): void {
    const { mode } = useTheme();

    useEffect(() => {
        const embedOrigin = (() => {
            const base = (import.meta.env.VITE_ACH_EMBED_URL || '').replace(/\/+$/, '');
            try {
                return base ? new URL(base).origin : '';
            } catch {
                return '';
            }
        })();
        if (!embedOrigin) return;

        const message = { type: 'theme-mode' as const, mode };
        const post = () => {
            document.querySelectorAll('iframe').forEach((frame) => {
                if (!frame.src.startsWith(embedOrigin)) return;
                frame.contentWindow?.postMessage(message, embedOrigin);
            });
        };

        post();
        // An iframe mounted in the same commit as the toggle may not have a
        // contentWindow ready yet; one retry on the next frame covers it
        // without turning this into a polling loop.
        const raf = window.requestAnimationFrame(post);
        return () => window.cancelAnimationFrame(raf);
    }, [mode]);
}
